import { useMemo } from "react";
import { HelpCircle, Info, Scissors } from "lucide-react";
import type { DraftGroup, DraftSubtask, EmailDraft, InboxDirectory } from "@/services/inbound-email.service";
import { DraftGroupCard } from "./DraftGroupCard";

const KIND_LABEL = { question: "Question", fyi: "FYI", praise: "Praise" } as const;

/** Draft editor shared by the inbox and the settings test run. */
export function DraftTree({
	draft,
	directory,
	editable,
	onGroupChange = () => {},
	onSubtaskChange = () => {},
}: {
	draft: EmailDraft;
	directory: InboxDirectory;
	editable: boolean;
	onGroupChange?: (gi: number, patch: Partial<DraftGroup>) => void;
	onSubtaskChange?: (gi: number, si: number, patch: Partial<DraftSubtask>) => void;
}) {
	const userOptions = useMemo(
		() => directory.users.map((u) => ({ value: u.id, label: u.full_name || u.email, description: u.email })),
		[directory.users],
	);
	const projectOptions = useMemo(
		() => directory.projects.map((p) => ({ value: p.id, label: p.key ? `${p.name} (${p.key})` : p.name })),
		[directory.projects],
	);

	return (
		<div className="space-y-4">
			{draft.summary && <p className="text-sm text-muted-foreground">{draft.summary}</p>}
			{draft.body_truncated && (
				<p className="flex items-center gap-1.5 text-xs text-warning">
					<Scissors className="h-3.5 w-3.5" aria-hidden />
					The email was too long and only its first part was read.
				</p>
			)}

			{draft.groups.length === 0 ? (
				<p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted">
					The agent found no tasks in this email.
				</p>
			) : (
				draft.groups.map((g, gi) => (
					<DraftGroupCard
						key={gi}
						index={gi}
						group={g}
						userOptions={userOptions}
						projectOptions={projectOptions}
						editable={editable}
						onChange={(patch) => onGroupChange(gi, patch)}
						onSubtaskChange={(si, patch) => onSubtaskChange(gi, si, patch)}
					/>
				))
			)}

			{draft.non_tasks.length > 0 && (
				<section aria-label="Not tasks" className="rounded-xl border border-border bg-muted-subtle/40 p-4">
					<h3 className="mb-2 text-sm font-medium text-foreground">Not turned into tasks</h3>
					<ul className="space-y-1.5">
						{draft.non_tasks.map((n, i) => (
							<li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
								{n.kind === "question" ? (
									<HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
								) : (
									<Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
								)}
								<span>
									<span className="font-medium text-foreground">{KIND_LABEL[n.kind]}:</span> {n.text}
								</span>
							</li>
						))}
					</ul>
				</section>
			)}
		</div>
	);
}
